import { z } from 'zod'

// Inbox (package flag `mailbox`): mail Cloudflare Email Routing hands to the API Worker for the site's own domain.
// Mail lives only in the CMS (D1 rows, R2 objects under `mail/`); nothing is forwarded. Every size is capped.

export const MAILBOX_LIMITS = {
  /** Messages larger than this are refused at SMTP time. */
  messageBytes: 10 * 1024 * 1024,
  /** Attachments stored per message; the rest are listed as skipped. */
  attachments: 10,
  /** Largest attachment stored; larger ones are listed as skipped. */
  attachmentBytes: 8 * 1024 * 1024,
  /** Attachment rows kept per message (stored and skipped together). */
  attachmentRows: 25,
  /** Text and HTML bodies are each cut at this many bytes (UTF-8). */
  bodyBytes: 500 * 1024,
  /** Messages kept; the oldest are deleted (rows and R2 objects) when a new one passes it. */
  messages: 5000,
  /** Raw size of all kept messages together; the oldest are deleted past it (R2 holds about twice this: .eml + attachments). */
  storageBytes: 1024 * 1024 * 1024,
  /** Replies sent from the CMS per site in any rolling 24 hours (sent and failed attempts both count). */
  repliesPerDay: 100,
  /** Characters in a reply body, quoted original included. */
  replyChars: 50_000,
  /** Cc addresses on one reply. */
  replyCc: 10,
  /** New emails (not replies) one person may send from the CMS in any rolling 24 hours, to stop accidents. */
  newPerUserPerDay: 50,
  /** Characters of a related message's text shown in a thread. */
  threadTextChars: 4000,
} as const

/** `in`: received through Email Routing. `out`: a reply or a new email sent from the CMS. */
export const MailDirectionSchema = z.enum(['in', 'out'])
export type MailDirection = z.infer<typeof MailDirectionSchema>

/** Outgoing only: `sent`, or `failed` (kept so nothing written is lost). Empty for received mail. */
export const MailStatusSchema = z.enum(['', 'sent', 'failed'])
export type MailStatus = z.infer<typeof MailStatusSchema>

/** Why an attachment was not stored. Empty: it was. */
export const MailSkipReasonSchema = z.enum(['', 'too_large', 'too_many', 'store_failed'])
export type MailSkipReason = z.infer<typeof MailSkipReasonSchema>

export const MailAttachmentSchema = z.object({
  id: z.string(),
  position: z.number().int(),
  filename: z.string(),
  contentType: z.string(),
  size: z.number().int(),
  /** False when the attachment was skipped (see `skipped`): only its name and size are known. */
  stored: z.boolean(),
  skipped: MailSkipReasonSchema,
})
export type MailAttachment = z.infer<typeof MailAttachmentSchema>

export const MailSummarySchema = z.object({
  id: z.string(),
  /** Envelope recipient, e.g. hello@example.com. */
  recipient: z.string(),
  fromAddress: z.string(),
  fromName: z.string(),
  subject: z.string(),
  receivedAt: z.string(),
  read: z.boolean(),
  /** SPF or DMARC failed, or the sender marked it as spam. */
  spam: z.boolean(),
  size: z.number().int(),
  attachmentCount: z.number().int(),
  direction: MailDirectionSchema,
  status: MailStatusSchema,
  /** To header ("Name <address>, …"); for a sent reply, who it went to. */
  toHeader: z.string(),
  /** Sent to an address that is not in the address list (only admins ever see these). */
  catchAll: z.boolean().default(false),
})
export type MailSummary = z.infer<typeof MailSummarySchema>

/** A sent (or failed) reply as shown in the thread under the original. */
export const MailReplySchema = z.object({
  id: z.string(),
  fromAddress: z.string(),
  fromName: z.string(),
  toHeader: z.string(),
  ccHeader: z.string(),
  subject: z.string(),
  text: z.string(),
  status: MailStatusSchema,
  error: z.string(),
  sentAt: z.string().nullable(),
  createdAt: z.string(),
})
export type MailReply = z.infer<typeof MailReplySchema>

/** Another message of the same conversation, shown under the one opened (text cut at MAILBOX_LIMITS.threadTextChars). */
export const MailThreadItemSchema = MailReplySchema.extend({ direction: MailDirectionSchema, recipient: z.string() })
export type MailThreadItem = z.infer<typeof MailThreadItemSchema>

export const MailMessageSchema = MailSummarySchema.extend({
  envelopeFrom: z.string(),
  ccHeader: z.string(),
  replyTo: z.string(),
  /** The Date header, ISO when it parses. */
  sentAt: z.string().nullable(),
  messageId: z.string(),
  text: z.string(),
  html: z.string(),
  textTruncated: z.boolean(),
  htmlTruncated: z.boolean(),
  /** The original .eml is kept and can be downloaded. */
  hasRaw: z.boolean(),
  skippedAttachments: z.number().int(),
  /** Set when the message could not be fully parsed or stored. */
  problem: z.string(),
  attachments: z.array(MailAttachmentSchema),
  /** Why sending a reply failed (outgoing, status `failed`). */
  error: z.string(),
  /** Outgoing: the received message this replies to (null once it was deleted, or for received mail). */
  parentId: z.string().nullable(),
  /** Received: the replies sent to it from the CMS, oldest first. */
  replies: z.array(MailReplySchema),
  /**
   * The rest of the conversation, matched by Message-ID: what this message answers (its In-Reply-To / References)
   * and what answers it, both directions, oldest first. E.g. a new email from the CMS and the reply that came back.
   */
  thread: z.array(MailThreadItemSchema).default([]),
})
export type MailMessage = z.infer<typeof MailMessageSchema>

export const MailListSchema = z.object({
  items: z.array(MailSummarySchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
  /** Unread messages this person can see (every address for admins, their own addresses for anyone else). */
  unread: z.number().int(),
  /** Addresses that have received mail and this person can see, for the filter. */
  recipients: z.array(z.string()),
  /** Admins: every address and the catch-all. Anyone else: only the addresses they are given. */
  seesAll: z.boolean().default(false),
  /** The addresses in the list this person can see (for admins all of them). */
  addresses: z.array(z.string()).default([]),
  /** Admins only: unread mail to addresses that are not in the list. Null for everyone else. */
  catchAllUnread: z.number().int().nullable().default(null),
})
export type MailList = z.infer<typeof MailListSchema>

/** One line, no control characters: it becomes a header. */
const headerLine = (max: number) =>
  z
    .string()
    .max(max)
    .transform((v) => v.replace(/[\r\n\t]+/g, ' ').trim())

/** POST /api/admin/mailbox/:id/reply. Plain text only; the HTML part is built from it, escaped. */
export const ReplyMailRequestSchema = z.object({
  body: z.string().max(MAILBOX_LIMITS.replyChars).refine((v) => v.trim().length > 0, 'Write a reply first'),
  cc: z.array(z.email().max(320)).max(MAILBOX_LIMITS.replyCc).default([]),
  /** Defaults to "Re: <original subject>". */
  subject: headerLine(500).optional(),
})
export type ReplyMailRequest = z.input<typeof ReplyMailRequestSchema>

/** POST /api/admin/mailbox/compose: a new plain-text email to one address, from one of the site's own addresses. */
export const ComposeMailRequestSchema = z.object({
  /** One of the addresses this person may use (the mailbox's own address). */
  from: z.string().trim().toLowerCase().max(320),
  to: z.string().trim().max(320).pipe(z.email('Enter one email address, like name@example.com')),
  subject: headerLine(500).refine((v) => v.length > 0, 'Write a subject'),
  body: z.string().max(MAILBOX_LIMITS.replyChars).refine((v) => v.trim().length > 0, 'Write the email first'),
})
export type ComposeMailRequest = z.input<typeof ComposeMailRequestSchema>

/** The answer to a new email: the stored row (sent, or failed and kept). */
export const ComposeMailResultSchema = MailReplySchema.extend({ recipient: z.string() })
export type ComposeMailResult = z.infer<typeof ComposeMailResultSchema>

export const UpdateMailRequestSchema = z.object({ read: z.boolean() })
export type UpdateMailRequest = z.infer<typeof UpdateMailRequestSchema>

// Mailbox addresses (#mailbox-addresses): the site's own addresses (sales@, admin@ …) and who may use each. A person
// sees, reads, answers, marks and deletes only the mail of the addresses they are given; admins see every address and
// the catch-all (mail to an address not in the list, which is kept, never dropped).

/** Local part and domain, lowercase, no display name. */
export const MailAddressValueSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .regex(/^[a-z0-9.!#$%&'*+/=?^_`{|}~-]{1,64}@[a-z0-9-]+(\.[a-z0-9-]+)+$/, 'Enter an address like sales@example.com')

export const MailAddressUserSchema = z.object({ id: z.string(), name: z.string(), email: z.string() })

export const MailAddressSchema = z.object({
  id: z.string(),
  address: z.string(),
  label: z.string(),
  /** The people given this address (admins see it anyway and are not listed). */
  users: z.array(MailAddressUserSchema),
  createdAt: z.string(),
})
export type MailAddress = z.infer<typeof MailAddressSchema>

/** Someone who could be given an address: an active, non-admin account. `canOpen`: their role includes the mailbox. */
export const MailAddressCandidateSchema = MailAddressUserSchema.extend({ roleName: z.string(), canOpen: z.boolean() })
export type MailAddressCandidate = z.infer<typeof MailAddressCandidateSchema>

export const MailAddressListSchema = z.object({
  items: z.array(MailAddressSchema),
  /** The domains an address may be on: the site's accepted mail domains. */
  domains: z.array(z.string()),
  candidates: z.array(MailAddressCandidateSchema),
})
export type MailAddressList = z.infer<typeof MailAddressListSchema>

export const MAIL_ADDRESS_LIMITS = {
  /** Addresses per site. */
  addresses: 50,
  /** People per address. */
  usersPerAddress: 100,
} as const

export const CreateMailAddressRequestSchema = z.object({
  address: MailAddressValueSchema,
  label: z.string().trim().max(80).default(''),
  userIds: z.array(z.string().max(64)).max(MAIL_ADDRESS_LIMITS.usersPerAddress).default([]),
})
export type CreateMailAddressRequest = z.input<typeof CreateMailAddressRequestSchema>

export const UpdateMailAddressRequestSchema = z.object({
  label: z.string().trim().max(80).optional(),
  /** Exactly who may use the address from now on. */
  userIds: z.array(z.string().max(64)).max(MAIL_ADDRESS_LIMITS.usersPerAddress).optional(),
})
export type UpdateMailAddressRequest = z.input<typeof UpdateMailAddressRequestSchema>
